import { useCallback, useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { CURRENT_JOB_ID_KEY } from "@/contexts/ActiveJobContext"
import {
  AnonymousVisitor,
  VisitorUpdateData,
  createAnonymousVisitor,
  updateAnonymousVisitor,
} from "@/services/anonymousVisitorService"

// Local storage keys
export const VISITOR_ID_KEY = "anonymousVisitorId"
export const FORM_STARTED_KEY = "formStarted"
export const FORM_COMPLETED_KEY = "formCompleted"

/**
 * Custom hook for managing anonymous visitor tracking
 *
 * This hook handles:
 * 1. Creating a new visitor on first visit
 * 2. Updating visitor information for form progress
 * 3. Tracking form completion status
 */
export const useAnonymousVisitor = () => {
  const queryClient = useQueryClient()
  const [visitorId, setVisitorId] = useState<string | null>(localStorage.getItem(VISITOR_ID_KEY))

  // Mutation for creating a new anonymous visitor
  const createVisitorMutation = useMutation({
    mutationFn: createAnonymousVisitor,
    onSuccess: (data) => {
      if (data.success && data.visitor?.id) {
        // Save visitor ID to local storage
        localStorage.setItem(VISITOR_ID_KEY, data.visitor.id)
        setVisitorId(data.visitor.id)
      }
    },
  })

  // Mutation for updating an existing anonymous visitor
  const updateVisitorMutation = useMutation({
    mutationFn: updateAnonymousVisitor,
    onSuccess: (data) => {
      if (data.success && data.visitor) {
        // Update visitor data in cache if needed
        queryClient.setQueryData(["visitor", data.visitor.id], data.visitor)
      }
    },
  })

  // Function to create a new visitor
  const createVisitor = useCallback(
    async (customData?: Partial<AnonymousVisitor>) => {
      // If we already have a visitor ID, no need to create a new one
      if (visitorId) return visitorId

      try {
        // Create visitor data object with device info
        const jobId = localStorage.getItem(CURRENT_JOB_ID_KEY) || undefined
        const visitorData: AnonymousVisitor = {
          device_info: {
            browser: navigator.userAgent,
            os: navigator.platform,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
          },
          ...(jobId ? { job_id: jobId } : {}),
          ...customData,
        }

        // Create the visitor
        const result = await createVisitorMutation.mutateAsync(visitorData)

        return result.visitor?.id
      } catch (error) {
        console.error("Error creating visitor:", error)
        return null
      }
    },
    [visitorId, createVisitorMutation],
  )

  // Manually update visitor with custom data
  const updateVisitor = useCallback(
    (updateData: VisitorUpdateData) => {
      if (!visitorId && !updateData.id) return

      updateVisitorMutation.mutate({
        id: updateData.id || visitorId,
        ...updateData,
      })
    },
    [visitorId, updateVisitorMutation],
  )

  // Form tracking methods
  const markFormStarted = useCallback(() => {
    // Check if form has already been started in this session
    const formStarted = localStorage.getItem(FORM_STARTED_KEY)
    if (formStarted === "true") {
      return // Form already started, don't start again
    }
    const visitorId = localStorage.getItem(VISITOR_ID_KEY)
    // If we have a visitor ID, update the record in the backend
    if (visitorId) {
      updateVisitor({
        id: visitorId,
        started_form: true,
        started_form_at: new Date().toISOString(),
        job_id: localStorage.getItem(CURRENT_JOB_ID_KEY) || undefined,
      })
      localStorage.setItem(FORM_STARTED_KEY, "true")
    }
  }, [updateVisitor])

  const markFormCompleted = useCallback(() => {
    // Check if form has already been completed in this session
    const formCompleted = localStorage.getItem(FORM_COMPLETED_KEY)
    if (formCompleted === "true") {
      return // Form already completed, don't mark again
    }

    // Mark as completed in local storage
    localStorage.setItem(FORM_COMPLETED_KEY, "true")
    const visitorId = localStorage.getItem(VISITOR_ID_KEY)
    // If we have a visitor ID, update the record in the backend
    if (visitorId) {
      updateVisitor({
        id: visitorId,
        completed_form: true,
        completed_form_at: new Date().toISOString(),
        job_id: localStorage.getItem(CURRENT_JOB_ID_KEY) || undefined,
      })
    }
  }, [updateVisitor])

  return {
    visitorId,
    isLoading: createVisitorMutation.isPending || updateVisitorMutation.isPending,
    error: createVisitorMutation.error || updateVisitorMutation.error,
    updateVisitor,
    markFormStarted,
    markFormCompleted,
    createVisitor,
    createVisitorMutation,
  }
}
